import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { useAdminAutoTrades } from '_hooks/useAdminAutoTrades'
import { ErrorMessage } from 'components/common/ErrorMessage';
import { Spinner } from 'components/common/Spinner';
import { useSearchParams } from 'react-router-dom';
import {
  AutoTradeManagementTypeEnumHelper,
  AutoTradePositionTypeEnum,
  AutoTradePositionTypeEnumHelper,
  AutoTradeStatusEnum,
  AutoTradeStatusEnumHelper, AutoTradeStatusReasonEnumHelper, IAutoTrade,
  IBKRSide,
  NewYorkTz,
  TriggerExitTypeEnumHelper,
} from 'predictagram-lib';
import { IAutoTradeLivePrice, IAutoTradeSearchFilter, adminApiServiceCommon } from "../../../services/AdminApiService";
import { MessengerContext } from "../../common/messenger";
import { StatusFilter } from './filters/StatusFilter';
import { SideFilter } from './filters/SideFilter';
import { IDateRangeFilter, OpenedFilter } from './filters/OpenedFilter';
import { PositionTypeFilter } from './filters/PositionTypeFilter';
import { PresetDateRange, PresetDateRangeFilter } from './filters/PresetDateRangeFilter';
import { useExcelDownload } from '_hooks/useExcelDownload';

import { SortUp, SortDown, FunnelFill, Funnel, Grid3x2, GraphUpArrow, ArrowClockwise, PlusCircle, XCircle } from 'react-bootstrap-icons'
import { ISortColumn, TableColumn, TradeModel } from 'models/trade.model';
import { SymbolsWatchList } from './SymbolsWatchList';
import { Helper } from '_utils';
import { StockSymbolFilter } from './filters/StockSymbolFilter';
import { ExitOverlay } from './ExitOverlay';
import { LinkAdminTradeSetup } from './LinkAdminTradeSetup';
import { ManualTradeModal } from './ManualTradeModal';
import { ChartOverlay } from './ChartOverlay';


export const formatUSCurrency = (n: number | undefined) => {
  if (!n) {
    return ""
  }
  return Helper.formatUSCurrency(n);
}

export const colorize = (v: string | number, suffix?: string | JSX.Element): JSX.Element => {
  const value = typeof v === "string" ? parseFloat(v.replace(/[^0-9.-]/g, '')) : v;
  const className = value < 0 ? "text-red" : "text-green";
  return <span className={`fw-bold ${className}`}>{v}{suffix}</span>
}

export interface ITradeLivePrice extends IAutoTradeLivePrice {
  id: number,
}

export const calcUnrealizedProfitLossOptionFormatted = (trade: IAutoTrade, currentPrice: number, formatted: boolean) => {
  if (trade.ibkrOpenOrder) {
    const result = TradeModel.calcUnrealizedProfitLossOption(trade, currentPrice);
    return result ? 
      (formatted ? colorize(Helper.formatUSCurrency(result)) : result) : undefined
  }
  return undefined;
}

export const ProfitLoss: React.FunctionComponent<{trades: IAutoTrade[]}> = ({trades}) => {
  const profitLoss = TradeModel.getTradesProfitLoss(trades);
  return <div className="d-flex flex-column gap-1 text-end">
    <div className="fw-bold">Realized P/L</div>
    {colorize(formatUSCurrency(profitLoss.profitLoss))}
    {profitLoss.initialValue && colorize(TradeModel.getProfitLossPct(profitLoss.initialValue, profitLoss.finalValue).toFixed(2), '%')}
  </div>
}

export const UnrealizedProfitLossActive: React.FunctionComponent<{trades: IAutoTrade[], livePrices: ITradeLivePrice[]}> = ({trades, livePrices}) => {
  return <div className="d-flex flex-column gap-1 text-end">
    <div className="fw-bold">Active Unrealized P/L</div>
    {colorize(formatUSCurrency(TradeModel.getUrealizedProfitLossOptionTrades(trades, livePrices)))}
  </div>
}

export const ClosedShares: React.FunctionComponent<{trades: IAutoTrade[]}> = ({trades}) => {
  return <div className="d-flex flex-column gap-1 text-end">
    <div className="fw-bold">Closed Shares</div>
    {TradeModel.getClosedShares(trades).toLocaleString('en-us')}
  </div>
}

export const ActiveShares: React.FunctionComponent<{trades: IAutoTrade[]}> = ({trades}) => {
  return <div className="d-flex flex-column gap-1 text-end">
    <div className="fw-bold">Active Shares</div>
    {TradeModel.getActiveShares(trades).toLocaleString('en-us')}
  </div>
}


export const ActiveInvested: React.FunctionComponent<{trades: IAutoTrade[]}> = ({trades}) => {
  return <div className="d-flex flex-column gap-1 text-end">
    <div className="fw-bold">Total Invested</div>
    {formatUSCurrency(TradeModel.getActiveTotalCost(trades))}
  </div>
}

export const MaxTotalInvested: React.FunctionComponent<{trades: IAutoTrade[]}> = ({trades}) => {
  return <div className="d-flex flex-column gap-1 text-end">
    <div className="fw-bold">Max Total Invested</div>
    {formatUSCurrency(TradeModel.getMaxTotalInvested(trades))}
  </div>
}

export const TradesSummary: React.FunctionComponent<{ trades: IAutoTrade[], livePrices: ITradeLivePrice[] }> = ({ trades, livePrices }) => {
  return <div className="d-flex flex-column gap-1">
    <div className="d-flex flex-row gap-3 text-12">
      <ProfitLoss trades={trades} />
      <ClosedShares trades={trades} />
      <UnrealizedProfitLossActive trades={trades} livePrices={livePrices} />
      <ActiveShares trades={trades} />
      <ActiveInvested trades={trades} />
      <MaxTotalInvested trades={trades} />
    </div>
  </div>
}

export const TradesListPage: React.FunctionComponent = () => {

  const msgrContext = useContext(MessengerContext);
  const [searchParams] = useSearchParams();
  const setupId = searchParams.get('setupId');

  // filters
  const [statusFilter, setStatusFilter] = useState<AutoTradeStatusEnum | undefined>(undefined);
  const [sideFilter, setSideFilter] = useState<IBKRSide | undefined>(undefined);
  const [positionTypeFilter, setPositionTypeFilter] = useState<AutoTradePositionTypeEnum | undefined>(undefined);
  const [presetDateRangeFilter, setPresetDateRangeFilter] = useState<PresetDateRange | undefined>(setupId ? undefined : PresetDateRange.TODAY);
  const [openedFilter, setOpenedFilter] = useState<IDateRangeFilter | undefined>(TradeModel.getOpenedFilterFromPreset(presetDateRangeFilter));
  const [setupFilter, setSetupFilter] = useState<number | undefined>(undefined);
  const [securityFilter, setSecurityFilter] = useState<string | undefined>(undefined);
  const [statusReasonFilter, setStatusReasonFilter] = useState<number | undefined>(undefined);

  const [stockSymbolFilter, setStockSymbolFilter] = useState<number | undefined>(undefined);
  const [excludeStockSymbol, setExcludeStockSymbol] = useState<boolean>(false);

  const [livePrices, setLivePrices] = useState<ITradeLivePrice[]>([]);
  const [tradeSetupId, setTradeSetupId] = useState<number | undefined>(undefined);

  const [isAdding, setIsAdding] = useState<boolean>(false);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  const tradeSetupCellRef = useRef<Array<HTMLDivElement | null>>([]);
  const exitTradeOverlayRef = useRef<HTMLDivElement | null>(null);
  const [exitOverlayPosition, setExitOverlayPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 });

  const securityCellRef = useRef<Array<HTMLDivElement | null>>([]);
  const chartOverlayRef = useRef<HTMLDivElement | null>(null);
  const [chartOverlayPosition, setChartOverlayPosition] = useState<{ top: number, left: number }>({ top: 0, left: 0 });

  const [activeTrade, setActiveTrade] = useState<IAutoTrade | undefined>(undefined);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [showCloseModal, setShowCloseModal] = useState<boolean>(false);
  const [activeTradeOverlay, setActiveTradeOverlay] = useState<IAutoTrade | undefined>(undefined);

  const searchFilter: IAutoTradeSearchFilter = {
    startTime: openedFilter?.startTime,
    endTime: openedFilter?.endTime,
  }
  const api = useAdminAutoTrades(searchFilter);

  const [data, setData] = useState<IAutoTrade[]>([]);
  const [filteredData, setFilteredData] = useState<IAutoTrade[]>([]);

  const [symbols, setSymbols] = useState<number[]>([]);
  const [showSymbols, setShowSymbols] = useState<boolean>(false);

  // table columns
  const tableColumns: TableColumn[] = TradeModel.tradeListTableColumns;
  // sort
  const defaultSort: ISortColumn = { tableColumn: tableColumns[0], direction: "asc" };
  const [sortColumn, setSortColumn] = useState<ISortColumn>(defaultSort);

  const mapper = (item: any) => {
    return {
      ...item,
      openedOn: NewYorkTz.format(new Date(item.openAt * 1000)).monthNameDateTime(),
      closedOn: item.closeAt ? NewYorkTz.format(new Date(item.closeAt * 1000)).monthNameDateTime() : '',
      openPrice: item.ibkrOpenOrder ? item.ibkrOpenOrder.orderPrice : '',
      closePrice: item.ibkrCloseOrder ? item.ibkrCloseOrder.orderPrice : '',
      ibkrOpenOrder: JSON.stringify(item.ibkrOpenOrder, null, 2),
      ibkrCloseOrder: JSON.stringify(item.ibkrCloseOrder, null, 2),
    }
  }
  const excelDownload = useExcelDownload<any>(mapper, 'trades');

  const loadPrices = useCallback(async () => {
    if (!api.apiState.isLoaded) {
      return;
    }
    if (api.data) {
      setData(api.data);
    }
  }, [api.data, api.apiState.isLoaded]);

  useEffect(() => {
    loadPrices();
  }, [loadPrices]);


  const updateFilteredList = useCallback(async () => {

    const filtered = api.data
      .filter(trade => setupId ? trade.setupId === parseInt(setupId) : true)
      .filter(trade => sideFilter ? trade.ibkrOpenOrder?.orderSide.toString() === sideFilter.toString() : true)
      .filter(trade => positionTypeFilter ? trade.positionTypeId.toString() === positionTypeFilter.toString() : true)
      .filter(trade => setupFilter ? trade.setupId === setupFilter : true)
      .filter(trade => securityFilter ? TradeModel.getSecurityName(trade.ibkrOpenOrder) === securityFilter : true)
      .filter(trade => stockSymbolFilter ? (excludeStockSymbol ? stockSymbolFilter.toString() !== trade.stockSymbolId.toString() : stockSymbolFilter.toString() === trade.stockSymbolId.toString()) : true)
      .filter(trade => statusReasonFilter ? trade.statusReasonId === statusReasonFilter : true)
    setFilteredData(filtered);
    setSymbols(filtered.map((v) => v.stockSymbolId).filter((v: number, i: number, array: number[]) => array.indexOf(v) === i));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api.data, securityFilter, setupFilter, statusReasonFilter]) //removed the following dependencies to avoid auto submit: positionTypeFilter, setupId, sideFilter, stockSymbolFilter

  // called whenever api state changes 
  useEffect(() => {
    if (api.apiState.isLoaded) {
      updateFilteredList();
    }
  }, [api.apiState.isLoaded, updateFilteredList])

  // loads the live prices into the filtered data set
  useEffect(() => {
    if (filteredData.length === 0) return;

    const _load = async () => {
      const livePrices: ITradeLivePrice[] = [];

      for (const d of filteredData) {
        // show prices only for a active and successful trades
        if (d.statusId === AutoTradeStatusEnum.ACTIVE && d.ibkrOpenOrder ) {
          try {
            const livePrice = await adminApiServiceCommon.getTradeLivePrices(d.id);
            livePrices.push({ id: d.id, ...livePrice })
            //console.debug(d.id, (d as any).livePrices);
          } catch (e) {
            console.error(e)
          }
        }
      }
      setLivePrices(livePrices);
    }
    _load();
  }, [filteredData])

  const updateList = useCallback(() => {
    const searchFilter: IAutoTradeSearchFilter = {
      startTime: openedFilter?.startTime,
      endTime: openedFilter?.endTime,
      autoTradeStatusIds: statusFilter ? [statusFilter] : undefined,
    }
    api.setFilter(searchFilter);
  }, [api, openedFilter?.startTime, openedFilter?.endTime, statusFilter])

  // called to udpate the start and end date selection
  useEffect(() => {
    const openedFilter = TradeModel.getOpenedFilterFromPreset(presetDateRangeFilter);
    setOpenedFilter(openedFilter);
  }, [presetDateRangeFilter])

  const closeTrade = async () => {
    if (!activeTrade) {
      msgrContext.setMessage({ body: 'Missing Trade. Nothing done.' });
      return;
    }
    const id = activeTrade.id;
    setIsDeleting(true);
    try {
      const res = await adminApiServiceCommon.closeTrade(id);
      if (res) {
        msgrContext.setMessage({ body: `Closed #${id}` });
        setPresetDateRangeFilter(PresetDateRange.TODAY);
        setStatusFilter(undefined);
        api.reload();
      } else {
        console.error(JSON.stringify(res, null, 2));
        throw Error(`unexpected error trying to close`);
      }
    } catch (error: any) {
      msgrContext.setMessage({ body: `${(error as Error).message}` });
    } finally {
      setIsDeleting(false);
      setShowCloseModal(false); 
    }
  }

  const addTrade = async () => {
    if (!activeTrade) {
      msgrContext.setMessage({ body: 'Missing Trade. Nothing done.' });
      return;
    }
    const id = activeTrade.id;
    setIsAdding(true);
    try {
      const res = await adminApiServiceCommon.addTrade(id);
      if (res) {
        msgrContext.setMessage({ body: `Added #${res.id}` });
        setPresetDateRangeFilter(PresetDateRange.TODAY);
        setStatusFilter(undefined);
        api.reload();
      } else {
        console.error(JSON.stringify(res, null, 2));
        throw Error(`unexpected error trying to add`);
      }
    } catch (error: any) {
      msgrContext.setMessage({ body: `${(error as Error).message}` });
    } finally {
      setIsAdding(false);
      setShowAddModal(false);
    }
  }

  const _setSortColumn = (tableColumn: TableColumn) => {
    const _sortColumn: ISortColumn = {
      tableColumn,
      direction: "asc"
    }

    if (sortColumn.tableColumn.name === tableColumn.name) {
      if (sortColumn.direction === "asc") {
        _sortColumn.direction = "desc"
      } else {
        _sortColumn.direction = "asc";
      }
    }
    setSortColumn(_sortColumn);
  }

  const renderSortIcon = (name: string) => {
    if (name === sortColumn.tableColumn.name) {
      if (sortColumn.direction === "asc") {
        return <SortUp />
      } else {
        return <SortDown />
      }
    }
    return <></>
  }

  const toggleTradeSetupFilter = (setupId: number) => {
    if (setupFilter === setupId) {
      setSetupFilter(undefined);
      return;
    }
    setSetupFilter(setupId);
  }

  const toggleSecurityFilter = (securityName: string | undefined) => {
    if (!securityName || securityName === securityFilter) {
      setSecurityFilter(undefined);
      return;
    }
    setSecurityFilter(securityName);
  }


  const getSecurityNameFilter = (trade: IAutoTrade, index: number) => {
    const ibkrOpenOrder = trade.ibkrOpenOrder;
    if (!ibkrOpenOrder) {
      return <></>;
    }
    const securityName = TradeModel.getSecurityName(ibkrOpenOrder);
    return <div className="d-flex gap-1 flex-row flex-nowrap">
      {securityName}
      <div ref={el => securityCellRef.current[index] = el} onMouseEnter={(e) => { onMouseOverSecurity(trade, index); e.stopPropagation() }}>
      <GraphUpArrow style={{color: 'blue'}}/>
      </div>
      <div role="button" onClick={() => toggleSecurityFilter(securityName)}>{securityFilter ? <FunnelFill color='gray' /> : <Funnel color='blue' />}</div>
    </div>
  }

  const renderLivePrices = (trade: IAutoTrade) => {
    const p = livePrices.find((t) => t.id === trade.id);
    if (!p) return <><td></td><td></td><td></td></>

    return (
      <>
        <td align="right">{formatUSCurrency(p.bidPrice)}</td>
        <td align="right">{formatUSCurrency(p.askPrice)}</td>
        <td align="right">{formatUSCurrency(p.lastPrice)}
          {[AutoTradeStatusEnum.ACTIVE, AutoTradeStatusEnum.CLOSED].includes(trade.statusId) && p.lastPrice && <div className="text-10 my-1">
            {calcUnrealizedProfitLossOptionFormatted(trade, p.lastPrice, true)}
          </div>
          }
        </td>
      </>
    );
  }

  const onMouseOverTradeCell = (tradeId: number, index: number) => {
    const overlay = exitTradeOverlayRef.current;
    const cell = tradeSetupCellRef.current[index];
    setActiveTradeOverlay(undefined);

    if (!overlay || !cell) {return;}

    const cellRect = cell.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    let top = cellRect.top;
    const left = cellRect.left + (cellRect.width * 0.5);

    const overlayHeight = 350;
    if (top + window.scrollY + overlayHeight > window.innerHeight) {
      top = window.innerHeight - overlayHeight;
    }

    setTradeSetupId(tradeId);
    setExitOverlayPosition({ top: top, left })
  }


  const onMouseOverSecurity = (trade: IAutoTrade, index: number) => {
    const overlay = chartOverlayRef.current;
    const cell = securityCellRef.current[index];
    setTradeSetupId(undefined);
    if (!overlay || !cell) {return;}



    const cellRect = cell.getBoundingClientRect();
    const overlayRect = overlay.getBoundingClientRect();
    let top = cellRect.top + window.scrollY;
    const left = cellRect.left + (cellRect.width * 0.5);

    // check if past the window bottom
    const chartHeight = 400; // @TODO: figure out why overlayRect.height is returning 0 and remove hard-code;

    if (top + chartHeight > window.innerHeight) {
      top = window.innerHeight - chartHeight;
    }

    setActiveTradeOverlay(trade);
    setChartOverlayPosition({ top: top, left })
  }


  const confirmAdd = (trade: IAutoTrade) =>  {
    setActiveTrade(trade);
    setShowAddModal(true);
  }

  const confirmClose = (trade: IAutoTrade) => {
    setActiveTrade(trade);
    setShowCloseModal(true);
  }

  if (api.apiState.isLoading) {
    return <Spinner />
  }

  if (api.apiState.isError) {
    return <ErrorMessage className="bg-white">{api.error}</ErrorMessage>
  }

  if (api.apiState.isLoaded) {
    return (
      <>
        {activeTrade &&  <ManualTradeModal
          show={showAddModal}
          handleClose={()=>setShowAddModal(false)}
          title='Add Trade'
          onConfirm={addTrade}
          trade={activeTrade}
          isSubmitting={isAdding}
        />}

        {activeTrade && <ManualTradeModal
          show={showCloseModal}
          handleClose={()=>setShowCloseModal(false)}
          title='Close Trade'
          onConfirm={closeTrade}
          trade={activeTrade}
          isSubmitting={isDeleting}
        />}

        <div>
          <div className="page-title my-3">Trades</div>
          <SymbolsWatchList show={showSymbols} setShow={setShowSymbols} symbols={symbols} />
          <div className="d-flex my-2 border rounded p-3 justify-content-between align-items-center">

            <div className="d-flex gap-3 text-12 align-items-start">
              <PresetDateRangeFilter value={presetDateRangeFilter} setValue={setPresetDateRangeFilter} />
              <OpenedFilter value={openedFilter} setValue={setOpenedFilter} />
              <SideFilter value={sideFilter} setValue={setSideFilter} />
              <PositionTypeFilter value={positionTypeFilter} setValue={setPositionTypeFilter} />
              <StatusFilter value={statusFilter} setValue={setStatusFilter} />
              <div className="form-group">
              <StockSymbolFilter value={stockSymbolFilter} setValue={setStockSymbolFilter} symbolIds={symbols} />
                <label className="my-1">
                <input type="checkbox" checked={excludeStockSymbol} onChange={()=>setExcludeStockSymbol(!excludeStockSymbol)} />
                <span className="ms-1">Exclude</span>
              </label>
              </div>
              <div className="align-self-start">
                <button type="button" className="btn btn-primary" onClick={() => updateList()}><ArrowClockwise /></button>
              </div>
            </div>

            <TradesSummary trades={filteredData} livePrices={livePrices} />

            {api.data && api.data.length > 0 &&
              <div>{excelDownload.renderIcon(data)}</div>
            }
          </div>

          <div className="fixed-table-head admin-trades-list">
            <table className="table table-striped table-hover">
              <thead>
                <tr>
                  {tableColumns.map((tableColumn, index) => {
                    if (!tableColumn.name) {
                      return <th key={`th-${index}`}><>{tableColumn.title}</></th>
                    }
                    return <th key={`th-${index}`} role="button" onClick={() => _setSortColumn(tableColumn)}>{tableColumn.title} {renderSortIcon(tableColumn.name)}</th>
                  })}
                </tr>
              </thead>
              <tbody>
                {filteredData.sort((a, b) => {
                  if (sortColumn.tableColumn.comparator) {
                    return sortColumn.tableColumn.comparator(a, b, sortColumn.direction);
                  }

                  // default comparator if no comparator
                  const valueA = a[sortColumn.tableColumn.name as keyof IAutoTrade].toString();
                  const valueB = b[sortColumn.tableColumn.name as keyof IAutoTrade].toString();
                  return TradeModel.compareHelper(valueA, valueB, sortColumn.direction);
                })
                  .map((trade, index) =>
                    <tr key={`trade-${trade.id}`}>
                      <td>{trade.id}</td>
                      <td>
                        <div className="d-flex flex-nowrap gap-1 align-items-center">
                          {trade.setupName}
                          <div ref={el=>tradeSetupCellRef.current[index] = el} onMouseEnter={(e) => {onMouseOverTradeCell(trade.setupId, index); e.stopPropagation()}}><Grid3x2 style={{color: 'blue'}} /></div>
                          <div role="button" onClick={() => toggleTradeSetupFilter(trade.setupId)}>{setupFilter ? <FunnelFill color='gray' /> : <Funnel color='blue' />}</div>
                          <div><LinkAdminTradeSetup tradeSetupId={trade.setupId} /></div>
                        </div>
                      </td>
                      <td className="text-nowrap">
                          <div className="d-flex flex-row flex-nowrap gap-1">
                            {getSecurityNameFilter(trade, index)}
                          </div>
                          <div className="text-10">
                          {trade.ibkrOpenOrder?.orderContractId && <div>Contract ID: {trade.ibkrOpenOrder?.orderContractId}</div>}
                          {trade.ibkrOpenOrder?.orderId && <div>Open Order Id: {trade.ibkrOpenOrder?.orderId}</div>}
                          {trade.ibkrCloseOrder?.orderId && <div>Close Order Id: {trade.ibkrCloseOrder?.orderId}</div>}
                          </div>
                      </td>
                      <td>{AutoTradePositionTypeEnumHelper.names.get(trade.positionTypeId)}</td>
                      <td>{trade.openQuantity}</td>
                      <td align="right">{formatUSCurrency(trade.ibkrOpenOrder?.orderPrice)}</td>
                      <td align="right">{formatUSCurrency(trade.ibkrCloseOrder?.orderPrice)}</td>
                      <td align="right">{trade.ibkrCloseOrder && trade.ibkrOpenOrder ? (colorize(formatUSCurrency(((trade.ibkrCloseOrder?.orderPrice - trade.ibkrOpenOrder?.orderPrice) * trade.closeQuantity * 100)))) : ''}</td>
                      <td>{new Date(trade.openAt * 1000).toLocaleString()}</td>
                      <td>
                        <div className="d-flex gap-2">
                          <button className="btn btn-primary text-13" onClick={() => confirmAdd(trade)}><PlusCircle title="Add Trade" /></button>
                          {trade.statusId === AutoTradeStatusEnum.ACTIVE &&
                            <button className="btn text-13 bg-red text-white" onClick={() => confirmClose(trade)}><XCircle title="Close Trade" /></button>
                          }
                        </div>
                      </td>
                      {renderLivePrices(trade)}
                      <td>{AutoTradeManagementTypeEnumHelper.names.get(trade.managementTypeId)}</td>
                      <td>{trade.closeAt ? new Date(trade.closeAt * 1000).toLocaleString() : ''}</td>
                      <td>
                        <div className="d-flex gap-1">
                        {AutoTradeStatusReasonEnumHelper.names.get(trade.statusReasonId)}
                        <div role="button" onClick={()=>statusReasonFilter ? setStatusReasonFilter(undefined) : setStatusReasonFilter(trade.statusReasonId)}>{statusReasonFilter ? <FunnelFill color='gray' /> : <Funnel color='blue' />}</div>
                        </div>
                      </td>
                      <td>{TriggerExitTypeEnumHelper.names.get(trade.closeTriggerTypeId)}</td>
                      <td>{AutoTradeStatusEnumHelper.names.get(trade.statusId)}</td>
                    </tr>
                  )}
              </tbody>
            </table>
          </div>

          <div ref={exitTradeOverlayRef} onMouseLeave={(e) => {
            setTradeSetupId(undefined);
            e.stopPropagation();
            }} style={{ position: "absolute", top: exitOverlayPosition.top, left: exitOverlayPosition.left }}>
            {tradeSetupId && <ExitOverlay tradeSetupId={tradeSetupId} onEscape={()=>setTradeSetupId(undefined)}/>}
          </div>

          <div ref={chartOverlayRef} onMouseLeave={(e) => {
            setActiveTradeOverlay(undefined);
            e.stopPropagation();
            }} style={{ position: "absolute", top: chartOverlayPosition.top, left: chartOverlayPosition.left }}>
            {activeTradeOverlay && <ChartOverlay symbolId={activeTradeOverlay.stockSymbolId} onEscape={()=>setActiveTrade(undefined)}/>}
          </div>


        </div>
      </>
    )
  }

  return <></>
}
